<template>
  <div class="w-100">
    <Jumbotron :item="style" type="styles" :name="$t('main.groupers.styles.title')" />
    <b-container fluid>
      <b-row>
        <b-col>
          <div v-if="isLoading" class="position-absolute w-100">
            <Loading />
          </div>
          <GridCSS v-else :items="styleContent" :send-vector-click="true">
            <template #default="slotProps">
              <NormalVectorOverlay :data="slotProps.data" />
            </template>
          </GridCSS>
          <GrouperPaginator
            name-page="style"
            :total-pages="styleTotalPages"
            :current-page="styleCurrentPage"
          />
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

import GridCSS from '@/components/Common/GridCSS';
import GrouperPaginator from '@/components/Groupers/GrouperPaginator';
import Jumbotron from '@/components/Common/Jumbotron';
import Loading from 'UI/Loading';
import NormalVectorOverlay from '@/components/Vectors/NormalVectorOverlay';

export default {
  nuxtI18n: {
    paths: {
      en: '/styles/:slug/:page',
      de: '/stilarten/:slug/:page',
      es: '/estilos/:slug/:page',
      pt: '/estilo/:slug/:page'
    }
  },
  components: {
    GridCSS,
    GrouperPaginator,
    Jumbotron,
    Loading,
    NormalVectorOverlay
  },
  async validate({ params, store }) {
    const currentPage = params.page ? parseInt(params.page) : 1;

    store.dispatch('styles/CLEAN_STYLES');
    await Promise.all([
      store.dispatch('styles/LOAD_STYLE', { slug: params.slug }),
      store.dispatch('styles/LOAD_STYLE_CONTENT', {
        slug: params.slug,
        page: currentPage
      })
    ]);
    return store.state.styles.styleContent.length;
  },
  async asyncData({ store, app }) {
    const { en, de, es, pt, cannonical } = store.state.styles.style.metadata;
    const slugs = {
      en: app.localePath({ name: 'styles-slug', params: { slug: en } }, 'en'),
      de: app.localePath({ name: 'styles-slug', params: { slug: de } }, 'de'),
      es: app.localePath({ name: 'styles-slug', params: { slug: es } }, 'es'),
      pt: app.localePath({ name: 'styles-slug', params: { slug: pt } }, 'pt')
    };

    let canonical = app.$canonicalPlugin(
      slugs[`${store.$i18n.locale}`],
      store.$i18n.locale,
      true,
      cannonical
    );

    return {
      seoFields: { canonical }
    };
  },
  head() {
    const metadata = this.$store.state.styles.styles.metadata;
    const i18nSeo = this.$seoPlugin({ hreflang: false, canonical: false });
    const openGraph = this.$openGraphPlugin(
      metadata?.metaTitle,
      'website',
      this.$route.path,
      metadata?.metaDescription,
      this.getImageFirstResult,
      this.$store.state.i18n.locale
    );
    return {
      title: metadata?.metaTitle,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: metadata?.metaDescription
        },
        { hid: 'robots', name: 'robots', content: 'noindex, follow' },
        ...openGraph
      ],
      link: [this.seoFields.canonical, ...i18nSeo.link]
    };
  },
  computed: {
    ...mapState('styles', [
      'style',
      'styleContent',
      'styleTotalPages',
      'styleCurrentPage',
      'isLoading'
    ]),
    ...mapGetters('styles', ['getImageFirstResult'])
  }
};
</script>

<style lang="scss" scoped>
h1 {
  font-size: 27px;
}
p {
  font-size: 16px;
}
.badge-secondary {
  background: #b8aea4;
}
</style>
